import type { AxiosError } from 'axios';

import { mapAxiosErrorToApiRequestError } from '../adapters/axios/axios-error-mapper';
import type { ApiErrorResponse } from '../types/api-error';
import {
  type ApiRequestError,
  isApiRequestError,
} from '../types/api-request-error';
import type { ErrorNormalizerHandler } from '../types/http-client.interface';

/** Verifica se o erro é do tipo Axios com response 4xx/5xx */
const isAxiosErrorWithResponse = (
  error: unknown
): error is AxiosError<ApiErrorResponse> =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  (error as AxiosError).response != null;

/**
 * Interceptor que normaliza erros 4xx/5xx para ApiRequestError.
 * É registrado automaticamente no carregamento de http-client.ts (sempre primeiro na cadeia).
 * As apps só precisam registrar seus handlers; eles recebem sempre ApiRequestError.
 *
 * @example
 * // No TanstackProvider - apenas os handlers da app (normalizador já está registrado)
 * httpClient.interceptors.response.use([...middlewaresResponseErrors]);
 */
export const createErrorNormalizerInterceptor = (): ErrorNormalizerHandler => {
  return (error: unknown): Promise<never> => {
    if (isApiRequestError(error)) {
      return Promise.reject(error);
    }

    if (!isAxiosErrorWithResponse(error)) {
      return Promise.reject(error);
    }

    const apiRequestError: ApiRequestError =
      mapAxiosErrorToApiRequestError(error);

    return Promise.reject(apiRequestError);
  };
};
